import React, { CSSProperties } from "react";
import "./builder-custom-numpad.scss";
import classNames from "classnames";
import { CustomNumpadProps } from "../../BuilderInterface";
import { CustomNumpadNode } from "../../../build/types";
import DynamicIcon from "../../DynamicIcon";
import {
    getColorsFromInteractiveStates,
    getCompoundStyle,
    getStateColorsForBuilder,
    getStateStyleForBuilder,
    getStylesFromInteractiveStates
} from "../../helpers";
import { useInteractionStates } from "../../../hooks/useInteractionStates";

const DEFAULT_NUMPAD_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "DEL"];

const DEFAULT_NUMPAD_GRID = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    [".", "0", "DEL"]
];

// Create a dynamic grid based on the columns parameter
const createNumpadGrid = (columns: number = 3, keys: string[] = DEFAULT_NUMPAD_KEYS) => {
    const numpadGrid = [];

    for (let i = 0; i < keys.length; i += columns) {
        numpadGrid.push(keys.slice(i, i + columns));
    }

    return numpadGrid;
};

const BuilderCustomNumpad: React.FC<CustomNumpadProps> = ({ node, builderProps, renderProps }: CustomNumpadProps) => {
    const { id, attr, stateManager } = node;
    const numpadNode = node as CustomNumpadNode;
    const customNumpadClass = classNames("custom-numpad", {
        [`${attr?.className}`]: !!attr?.className
    });

    const { setElement, states } = useInteractionStates<HTMLDivElement>();

    const numpadKeys = numpadNode.gridConfig ? createNumpadGrid(numpadNode.gridConfig.columns) : DEFAULT_NUMPAD_GRID;

    if (builderProps) {
        const { nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        const wrapperStyle = getCompoundStyle({
            node,
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
        });

        const keyStyle = getCompoundStyle({
            otherStyle: numpadNode.keyStyle,
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "keyStyle")
        });

        const baseIconStyle = numpadNode.deleteIcon?.style || {};
        const stateIconStyle = getStateStyleForBuilder(stateManager, nodeState, "deleteIcon_style") || {};
        const deleteIconStyle: CSSProperties = {
            ...baseIconStyle,
            pointerEvents: "none",
            ...stateIconStyle
        };

        const deleteIconColors =
            getStateColorsForBuilder(stateManager, nodeState, "deleteIcon_colors") || numpadNode.deleteIcon?.colors;

        return (
            <div
                {...attr}
                className={customNumpadClass}
                style={wrapperStyle}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                <div
                    className="numpad-buttons"
                    style={
                        numpadNode.gridConfig
                            ? {
                                  gridTemplateColumns: `repeat(${numpadNode.gridConfig.columns}, 1fr)`
                              }
                            : undefined
                    }
                >
                    {numpadKeys.map((row, rowIndex) => (
                        <div key={rowIndex} className="numpad-row">
                            {row.map((key, keyIndex) => (
                                <div
                                    key={keyIndex}
                                    className={`numpad-key ${key === "DEL" ? "delete-key" : ""}`}
                                    style={keyStyle}
                                >
                                    {key === "DEL" && numpadNode.deleteIcon?.src ? (
                                        <DynamicIcon
                                            name={numpadNode.deleteIcon.src}
                                            style={deleteIconStyle}
                                            colors={deleteIconColors}
                                        />
                                    ) : (
                                        <span>{key}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    } else {
        const wrapperStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"]
        });

        const keyStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: numpadNode.keyStyle,
            stateManager,
            paths: ["keyStyle"]
        });

        let DeleteIcon = (
            <svg style={{ width: "32px", height: "32px" }} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.5" y="0.5" width="17" height="17" rx="3.5" stroke="#ABA7AF" />
                <path
                    d="M5 5L13 13M5 13L13 5"
                    stroke="#ABA7AF"
                    strokeWidth="1.08333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );

        if (numpadNode.deleteIcon?.src) {
            const style = getStylesFromInteractiveStates({
                states,
                defaultStyle: numpadNode.deleteIcon?.style,
                stateManager,
                paths: ["numpadNode_deleteIcon_style"]
                // ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            const colors = getColorsFromInteractiveStates({
                states,
                stateManager,
                paths: ["numpadNode_deleteIcon_colors"],
                defaultColors: numpadNode.deleteIcon?.colors
                // ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            DeleteIcon = <DynamicIcon name={numpadNode.deleteIcon.src} style={style} colors={colors} />;
        }

        return (
            <div {...attr} className={customNumpadClass} style={wrapperStyle} id={id} ref={setElement}>
                <div
                    onKeyDown={renderProps?.handleKeyDown}
                    className="numpad-buttons"
                    style={
                        numpadNode.gridConfig
                            ? {
                                  gridTemplateColumns: `repeat(${numpadNode.gridConfig.columns}, 1fr)`
                              }
                            : undefined
                    }
                >
                    {numpadKeys.map((row, rowIndex) => (
                        <div key={rowIndex} className="numpad-row">
                            {row.map((key, keyIndex) => (
                                <div
                                    key={keyIndex}
                                    className={`numpad-key ${key === "DEL" ? "delete-key" : ""}`}
                                    style={keyStyle}
                                    onClick={
                                        key === "DEL"
                                            ? () => renderProps?.handleDelete?.()
                                            : () => renderProps?.handlePinInput?.(key)
                                    }
                                >
                                    {key === "DEL" && numpadNode.deleteIcon?.src ? DeleteIcon : <span>{key}</span>}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    }
};

export default BuilderCustomNumpad;
